import { colors } from "../../../ui/colors";
import { EditIcon } from "../../../ui/icons/edit";
import { SortIcon } from "../../../ui/icons/sort";
import { TrashIcon } from "../../../ui/icons/trash";
import Avatar from "../../../ui/molecules/avatar";
import Pills from "../../../ui/molecules/pills";
import { Dropdown } from "../../../ui/organisms/dropdown";
import { CustomTable } from "../../../ui/organisms/table";
import { ArrowsDown } from "../../../ui/organisms/table/arrowsDown";
import { ArrowsUp } from "../../../ui/organisms/table/arrowsUp";
import { Sort } from "../../../ui/organisms/table/sort";
import { UserDTO, UserRoleMap } from "../dto";

interface Props {
    data: {
        user: UserDTO,
        avatar: string | null
    }[]
    sortField?: string
    sortDirection: 'asc' | 'desc'
    onSort: (field: string) => void
    onEdit: (id: string) => void
    onDelete: (id: string) => void
}

export function UsersTable({
    data,
    sortField,
    sortDirection,
    onSort,
    onEdit,
    onDelete
}: Props) {

    const createSortableHeader = (label: string, field: string) => (
        <th key={field} className="text-left">
            <div className="flex gap-[4px] items-center">
                {label}
                <div
                    onClick={() => onSort(field)}
                    className="cursor-pointer hover:opacity-70"
                >
                    {sortField === field ? sortDirection === 'asc' ? <ArrowsUp color={colors.brandPrimary[600]} size={12} /> : <ArrowsDown color={colors.brandPrimary[600]} size={12} /> : <Sort size={14} color={colors.neutral[600]} />}
                </div>
            </div>
        </th>
    );

    return (
        <CustomTable
            asc={sortDirection === 'asc'}
            settedOrder={sortField}
            tableHeight='calc(100% - 48px)'
            prevOrderHeads={[
                <th key="avatar" className="text-left" />,
                createSortableHeader("Nome", "name"),
                createSortableHeader("Cognome", "surname"),
                createSortableHeader("Ruolo", "role"),
                createSortableHeader("Email", "email"),
                createSortableHeader("Telefono", "phone"),
                <th key="actions" className="text-left">
                    <div className="flex gap-[4px]" />
                </th>
            ]}
            postOrderHeads={[]}
            preferences={[]}
            setPreferences={() => { }}
            setOrderFilters={() => { }}
            rows={data.map(d => ({
                onClick: () => onEdit(d.user.id),
                key: d.user.id,
                data: [
                    {
                        item: <td key="avatar">
                            <Avatar
                                type="single"
                                size="sm"
                                shape="circle"
                                fullName={`${d.user.name} ${d.user.surname}`}
                                altTextInitials={`${d.user.name[0]}${d.user.surname[0]}`}
                                imageUrl={d.avatar ?? ""}
                            />
                        </td>,
                        group: 'preSort',
                        key: 'avatar'
                    },
                    {
                        item: <td key="name" className="max-w-[150px] truncate text-sm font-semibold text-neutral-600">
                            {d.user.name}
                        </td>,
                        group: 'preSort',
                        key: 'name'
                    },
                    {
                        item: <td key="surname" className="max-w-[150px] truncate text-sm font-semibold text-neutral-600">
                            {d.user.surname}
                        </td>,
                        group: 'preSort',
                        key: 'surname'
                    },
                    {
                        item: <td key="role" className="max-w-[150px]">
                            <Pills
                                size="sm"
                                label={UserRoleMap.get(d.user.role)!}
                                outline={true}
                                emphasis={false}
                                color="blue"
                            />
                        </td>,
                        group: 'preSort',
                        key: 'role'
                    },
                    {
                        item: <td key="email" className="max-w-[200px] truncate text-sm font-semibold text-neutral-600">
                            {d.user.email}
                        </td>,
                        group: 'preSort',
                        key: 'email'
                    },
                    {
                        item: <td key="phone" className="max-w-[150px] truncate text-sm font-semibold text-neutral-600">
                            {d.user.phone}
                        </td>,
                        group: 'preSort',
                        key: 'phone'
                    },
                    {
                        item: <td
                            key="actions"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            className="w-[20px] cursor-pointer">
                            <Dropdown
                                items={[
                                    {
                                        label: "Modifica",
                                        icon: <EditIcon color="" size={16} />,
                                        onClick: () => onEdit(d.user.id),
                                    },
                                    {
                                        label: "Cancella",
                                        icon: <TrashIcon color="" size={16} />,
                                        onClick: () => onDelete(d.user.id),
                                    },
                                ]}
                            />
                        </td>,
                        group: 'preSort',
                        key: 'actions'
                    },
                ]
            }))}
        />
    );
}